import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { I18nRun, I18nRunLog } from '../lib/api.js'
import { Modal, Spinner } from './ui/index.js'
import { cn } from '../lib/utils.js'

/**
 * Painel de status das traduções — um item por idioma, atualizando ao vivo
 * (polling de 3s enquanto houver algo 'running'):
 *   ✓ done · tudo traduzido
 *   ⚠ partial · algo ficou para trás (IA devolveu menos, sobras de teto)
 *   ✗ error · falhou (mensagem visível)
 * Cada item abre o log completo da execução.
 */
export function I18nRunsPanel({
  queryKey,
  fetchRuns,
  fetchLogs,
  showScope = false,
  emptyHint = 'Nenhuma tradução executada ainda — use os botões acima para iniciar.',
}: {
  queryKey: string[]
  fetchRuns: () => Promise<I18nRun[]>
  fetchLogs: (runId: string) => Promise<I18nRunLog[]>
  /** página da plataforma: distingue Base × Cardápio */
  showScope?: boolean
  emptyHint?: string
}) {
  const qc = useQueryClient()
  const [openRun, setOpenRun] = useState<I18nRun | null>(null)

  const { data: runs = [] } = useQuery({
    queryKey,
    queryFn: fetchRuns,
    // Sempre ao vivo: 2,5s enquanto algo executa; 5s em ocioso — pega runs
    // que nascem DEPOIS do clique (waitUntil cria a linha assincronamente) e
    // também as automáticas (salvar produto dispara tradução).
    refetchInterval: (query) =>
      (query.state.data ?? []).some((r) => r.status === 'running') ? 2500 : 5000,
  })

  const { data: logs = [], isLoading: loadingLogs } = useQuery({
    queryKey: [...queryKey, 'logs', openRun?.id],
    queryFn: () => fetchLogs(openRun!.id),
    enabled: !!openRun,
    // logs acompanham a execução aberta enquanto roda
    refetchInterval: openRun?.status === 'running' ? 3000 : false,
  })

  if (runs.length === 0) {
    return <p className="text-xs text-gray-400">{emptyHint}</p>
  }

  return (
    <div className="rounded-xl border border-gray-200 divide-y divide-gray-100">
      {runs.map((run) => (
        <div key={run.id} className="flex items-center gap-3 px-3 py-2.5">
          <StatusIcon status={run.status} />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-gray-900 truncate">
              {showScope && (
                <span className="text-gray-400 font-normal">
                  {run.scope === 'ui_base' ? 'Base · ' : 'Menu · '}
                </span>
              )}
              {run.locale}
              {(run as I18nRun & { tenant_name?: string | null }).tenant_name && (
                <span className="text-gray-400 font-normal">
                  {' '}
                  · {(run as I18nRun & { tenant_name?: string | null }).tenant_name}
                </span>
              )}
            </p>
            <p
              className={cn(
                'text-xs truncate',
                run.status === 'error'
                  ? 'text-red-600'
                  : run.status === 'partial'
                    ? 'text-amber-600'
                    : 'text-gray-500',
              )}
            >
              {run.status === 'running' ? (
                <>
                  traduzindo… {run.translated_items}
                  {run.total_items > 0 ? ` de ${run.total_items}` : ''} itens
                </>
              ) : (
                <>
                  {statusLabel(run)} · {run.translated_items}/{run.total_items} itens
                  {run.error ? ` — ${run.error}` : ''}
                </>
              )}
            </p>
          </div>
          <button
            onClick={() => {
              setOpenRun(run)
              // abre já com dados frescos
              void qc.invalidateQueries({ queryKey: [...queryKey, 'logs'] })
            }}
            className="text-xs font-medium text-indigo-600 hover:text-indigo-800 flex-shrink-0"
          >
            Logs
          </button>
        </div>
      ))}

      <Modal
        open={!!openRun}
        onClose={() => setOpenRun(null)}
        title={`Logs — ${openRun?.locale ?? ''}`}
      >
        <div className="space-y-1.5 max-h-80 overflow-y-auto min-w-[320px]">
          {loadingLogs && <Spinner className="w-4 h-4 text-indigo-400" />}
          {logs.length === 0 && !loadingLogs && (
            <p className="text-xs text-gray-400">Sem eventos registrados.</p>
          )}
          {logs.map((l) => (
            <div key={l.id} className="flex gap-2 text-xs items-start">
              <span
                className={cn(
                  'font-mono px-1.5 py-0.5 rounded flex-shrink-0',
                  l.level === 'error'
                    ? 'bg-red-50 text-red-700'
                    : l.level === 'warn'
                      ? 'bg-amber-50 text-amber-700'
                      : 'bg-gray-100 text-gray-500',
                )}
              >
                {levelLabel(l.level)}
              </span>
              <span className="text-gray-700 break-words">
                {formatTime(l.created_at)} — {l.message}
              </span>
            </div>
          ))}
        </div>
      </Modal>
    </div>
  )
}

function StatusIcon({ status }: { status: I18nRun['status'] }) {
  if (status === 'running') return <Spinner className="w-4 h-4 text-indigo-500 flex-shrink-0" />
  return (
    <span
      className={cn(
        'w-5 h-5 rounded-full flex items-center justify-center text-xs flex-shrink-0 font-bold',
        status === 'done'
          ? 'bg-green-100 text-green-700'
          : status === 'partial'
            ? 'bg-amber-100 text-amber-700'
            : 'bg-red-100 text-red-700',
      )}
    >
      {status === 'done' ? '✓' : status === 'partial' ? '!' : '×'}
    </span>
  )
}

function statusLabel(run: I18nRun): string {
  if (run.status === 'done') return 'concluída'
  if (run.status === 'partial') return 'parcial'
  if (run.status === 'error') return 'erro'
  return 'em andamento'
}

function levelLabel(level: I18nRunLog['level']): string {
  return level === 'error' ? 'ERRO' : level === 'warn' ? 'AVISO' : 'info'
}

function formatTime(ts: number): string {
  const d = new Date(ts < 1e12 ? ts * 1000 : ts)
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}
